/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState } from 'react';
import { Dialog } from 'components/molecules/Dialog';
import { Box } from 'components/base/layout/Box';
import { TextField } from 'components/base/inputs/TextField';
import { Typography } from 'components/base/dataDisplay/Typography';
import { Button } from 'components/atoms/Button';

import type { CSSProperties } from 'react';
import type { SxProps } from 'components/base/system/style';
import type { Theme } from 'components/base/system/theme';

const useStyles = (): Record<string, SxProps<Theme> | CSSProperties> => ({
  hr: {
    margin: '0 0',
    border: 'none',
    borderTop: '1px solid #ccc',
  },
  dialogActions: {
    padding: '16px 24px',
  },
});

interface ConfirmationModalProps {
  openModal?: boolean;
  setDeleteModal?: (value: boolean) => void;
  handleDelete?: () => void;
  deleteButtonLabel?: string;
  cancelButtonLabel?: string;
  buttonLoading?: boolean;
  confirmationTextDescription?: string;
  isDeleteConfirm?: boolean;
  isFirstButtonHidden?: boolean;
  isSecondButtonHidden?: boolean;
  isCloseHidden?: boolean;
  title?: React.ReactNode;
  isDivider?: boolean;
  content?: React.ReactNode;
  firstButton?: React.ReactNode;
  secondButton?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  handleCancel?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

/**
 * @deprecated Use the ConfirmationModal component from the components/molecules/Dialog package instead.
 */
export default function ConfirmationModal({
  openModal = false,
  setDeleteModal = () => {},
  handleDelete = () => {},
  deleteButtonLabel,
  cancelButtonLabel,
  buttonLoading,
  confirmationTextDescription,
  isDeleteConfirm,
  isFirstButtonHidden,
  isSecondButtonHidden,
  isCloseHidden,
  title,
  isDivider,
  content,
  firstButton,
  secondButton,
  maxWidth = 'xs',
  handleCancel,
  icon,
  className,
}: ConfirmationModalProps) {
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const classes = useStyles();

  const handleClose = () => {
    setDeleteModal(false);
    typeof handleCancel === 'function' && handleCancel();
  };

  return (
    <Dialog
      open={openModal}
      title={typeof title === 'string' ? title : undefined}
      dialogTitle={typeof title !== 'string' ? title : undefined}
      maxWidth={maxWidth === false ? 'xs' : maxWidth}
      onClose={handleClose}
      hideCloseButton={isCloseHidden}
      showHeaderDivider={isDivider}
      dialogStyles={className ? { className } : undefined}
      dialogContent={
        <>
          {isDivider && <hr style={classes.hr as CSSProperties} />}
          <Box
            display="flex"
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ width: 50, marginRight: 20 }}>
              {icon || (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="50"
                  width="50"
                  viewBox="0 0 24 24"
                  fill="#E13939"
                >
                  <path d="M4.47 21h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18c-.77 1.33.19 3 1.73 3zM12 14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" />
                </svg>
              )}
            </div>
            <Box sx={{ paddingTop: '10px' }}>{content}</Box>
          </Box>
          {isDeleteConfirm && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: '#F44336', marginBottom: '0.5rem' }}>
                {confirmationTextDescription}
              </Typography>
              <TextField
                sx={{
                  background: '#F5F5F5',
                  borderRadius: '4px',
                  border: error ? '1px solid red' : '',
                  width: 'fit-content',
                }}
                variant="outlined"
                type="text"
                value={text}
                onChange={({ target: { value } }) => {
                  setError('');
                  setText(value);
                }}
                autoFocus
              />
            </Box>
          )}
        </>
      }
      dialogActionStyles={classes.dialogActions as React.CSSProperties}
      dialogFooter={
        <>
          {firstButton ||
            (!isFirstButtonHidden && (
              <Button
                variant="outlinedSecondary"
                size="small"
                data-testid="confirmation-modal-cancel-btn"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleClose();
                }}
                sx={{
                  textTransform: 'capitalize',
                }}
              >
                {cancelButtonLabel || 'Cancel'}
              </Button>
            ))}

          {secondButton ||
            (!isSecondButtonHidden && (
              <Button
                variant="containedError"
                isLoading={buttonLoading}
                data-testid="confirmation-modal-delete-btn"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if ((!text || text !== 'DELETE') && isDeleteConfirm) {
                    setError('Field is required.');
                    return;
                  }
                  handleDelete();
                }}
                sx={{
                  textTransform: 'capitalize',
                }}
              >
                {deleteButtonLabel || 'Delete'}
              </Button>
            ))}
        </>
      }
    />
  );
}
